import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from "@angular/core";
import { PlannedRequestDto } from "../../../../shared/models/request/planned-request.dto";
import { YaMapComponent } from "angular8-yandex-maps/lib/components/ya-map/ya-map.component";
import { MapPointStatesEnum } from "./map-point-states.enum";
import { GetUsersGeoTracksRequest } from "../../../../shared/models/User/get-users-geo-tracks.request";
import { UserGeoTrackDto } from "../../../../shared/models/User/user-geo-track.dto";
import { UserGeoTrackPointDto } from "../../../../shared/models/User/user-geo-track-point.dto";
import { SecurityService } from "../../../../core/services/security.service";

interface GeoObjectConstructor {
    feature: any;
    options: any;
}

interface IRequestPoint {
    geometry: number[];
    properties: any;
    options: any;
    request: PlannedRequestDto;
}

@Component({
    selector: 'planning-map',
    templateUrl: './planning-map.component.html',
    styleUrls: [
        './planning-map.component.scss',
    ],
    encapsulation: ViewEncapsulation.None
})
export class PlanningMapComponent implements OnInit, OnDestroy {

    @ViewChild('yaMapComponent') yaMapComponent: any;

    @Input() planningMapComponentName: string = 'planningMapAngularComponent';
    @Input() showRoutes: boolean = false;

    @Output() setPerformerUser: EventEmitter<number> = new EventEmitter<number>();
    @Output() setVisitDate: EventEmitter<any> = new EventEmitter<any>();

    defaultMapPointOpacity: number = 0.43;

    requestPoints: IRequestPoint[] = [];

    polylines: GeoObjectConstructor[] = [];

    constructor(
        private changeDetector: ChangeDetectorRef,
        private securityService: SecurityService
    ) {
        this.showAllPerformerRequests = this.showAllPerformerRequests.bind(this);
        this.setRequestPerformerUser = this.setRequestPerformerUser.bind(this);
        this.resetRequestPerformerUser = this.resetRequestPerformerUser.bind(this);
    }

    ngOnInit(): void {
        window[this.planningMapComponentName] = this;
    }

    ngOnDestroy(): void {
        window[this.planningMapComponentName] = null;
    }

    refreshRequests(requests: PlannedRequestDto[] = []): void {
        this.requestPoints = requests.map(request => {
            let options: any = {
                iconLayout: 'default#image',
                iconImageSize: this.buildIconSize(request),
                iconColor: request.requestPerformerUserColor,
                iconImageHref: this.buildIconHref(request) //"https://static.vecteezy.com/system/resources/thumbnails/023/554/762/small_2x/red-map-pointer-icon-on-a-transparent-background-free-png.png"
            };

            if (this.hasSpecificPlanTime(request)) {
                options.iconImageOffset = this.buildIconOffset(request);
            }
            let properties = {
                hintContent: this.buildModalContent(request),
                balloonContent: this.buildModalContent(request)
            };
            let geometry = [request.latitude, request.longitude];

            return {
                options: options,
                properties: properties,
                geometry: geometry,
                request: request
            } as IRequestPoint;
        });

        this.changeDetector.detectChanges();
    }

    refreshTracks(tracks: UserGeoTrackDto[]): void {
        this.polylines = [];

        if (!tracks || tracks.length == 0) {
            return;
        }

        tracks.forEach(track => {
            this.polylines.push({
                feature: {
                    geometry: {
                        type: 'LineString',
                        coordinates: track.points.map(p => [p.latitude, p.longitude]),
                    },
                },
                options: {
                    strokeWidth: 2,
                    strokeOpacity: 0.6,
                    strokeStyle: 'dash',
                    strokeColor: track.colorCode
                },
            } as GeoObjectConstructor);

            if (track.points.length > 0) {
                const lastPoint = track.points[track.points.length - 1];

                let options = {
                    iconLayout: 'default#image',
                    iconImageSize: [50, 50],
                    iconColor: track.color,
                    iconImageHref: this.buildIconHrefTrack(track)
                };
                let properties = {
                    hintContent: this.buildModalContentTrack(track, lastPoint),
                    balloonContent: this.buildModalContentTrack(track, lastPoint)
                };
                let geometry = [lastPoint.latitude, lastPoint.longitude];

                const point = {
                    options: options,
                    properties: properties,
                    geometry: geometry,
                    // request: request
                } as IRequestPoint;

                this.requestPoints.push(point);
            }
        });

        this.changeDetector.detectChanges();
    }

    buildIconSize(request: PlannedRequestDto): number[] {
        if (this.hasSpecificPlanTime(request)) {
            switch (request.mapPointState) {
                case MapPointStatesEnum.default:
                    return [44, 62];
                case MapPointStatesEnum.disabled:
                    return [40, 58];
                case MapPointStatesEnum.enabled:
                    return [48, 66];
            }
        }

        return this.getRegularIconSize(request);
    }

    private getRegularIconSize(request: PlannedRequestDto): number[] {
        switch (request.mapPointState) {
            case MapPointStatesEnum.default:
                return [32, 32];
            case MapPointStatesEnum.disabled:
                return [28, 28];
            case MapPointStatesEnum.enabled:
                return [48, 48];
        }
    }

    buildIconOffset(request: PlannedRequestDto): number[] {
        const [width, height] = this.buildIconSize(request);

        if (this.hasSpecificPlanTime(request)) {
            const pinTipSvgY = 31;
            const timedViewBoxMinY = -14;
            const timedViewBoxHeight = 62;
            const pinTipY = Math.round((pinTipSvgY - timedViewBoxMinY) / timedViewBoxHeight * height);
            return [-Math.round(width / 2), -pinTipY];
        }

        return [-Math.round(width / 2), -height];
    }

    /** Масштаб маркера до размера обычной точки; часы и таблетка не масштабируются. */
    private getTimedPinScale(mapPointState: MapPointStatesEnum): number {
        const timedPinHeightInViewBox = 28;
        const timedViewBoxHeight = 62;

        let regularImageHeight: number;
        let timedImageHeight: number;

        switch (mapPointState) {
            case MapPointStatesEnum.default:
                regularImageHeight = 32;
                timedImageHeight = 62;
                break;
            case MapPointStatesEnum.disabled:
                regularImageHeight = 28;
                timedImageHeight = 58;
                break;
            default:
                regularImageHeight = 48;
                timedImageHeight = 66;
                break;
        }

        const regularPinPixels = regularImageHeight;
        const timedPinPixels = timedPinHeightInViewBox / timedViewBoxHeight * timedImageHeight;

        return regularPinPixels / timedPinPixels;
    }

    private hasSpecificPlanTime(request: PlannedRequestDto): boolean {
        return this.getAgreedVisitTimeText(request) != null;
    }

    private getAgreedVisitTimeText(request: PlannedRequestDto): string | null {
        if (request.plannedDate == null) {
            return null;
        }

        const plannedDate = new Date(request.plannedDate);
        if (isNaN(plannedDate.getTime())) {
            return null;
        }

        const hours = plannedDate.getHours();
        const minutes = plannedDate.getMinutes();

        if (hours === 0 && minutes === 0) {
            return null;
        }

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    private buildTimedIconHref(request: PlannedRequestDto): string {
        const hasPerformerUser = request.requestPerformerUserId != null && request.requestPerformerUserId != 0;
        const fillIconColor = hasPerformerUser && !this.isEmpty(request.requestPerformerUserColor)
            ? request.requestPerformerUserColor
            : '#465BFF';

        let opacity = 1;
        if (request.mapPointState == MapPointStatesEnum.disabled) {
            opacity = this.defaultMapPointOpacity;
        }

        const centerText = request.mapPointState == MapPointStatesEnum.enabled && request.planOrderNumber
            ? request.planOrderNumber.toString()
            : '';

        const agreedVisitTime = this.getAgreedVisitTimeText(request);

        const svgContent = this.buildTimedIconSvg(
            fillIconColor,
            opacity,
            centerText,
            agreedVisitTime,
            request.isFinal,
            this.getTimedPinScale(request.mapPointState)
        );

        return `data:image/svg+xml;base64,${btoa(svgContent)}`;
    }

    private buildTimedIconSvg(
        fillIconColor: string,
        opacity: number,
        centerText: string,
        timeText: string,
        isFinal: boolean,
        pinScale: number
    ): string {
        const pinTipY = 31;
        const timeLabelY = pinTipY + 3;
        const clockCy = 4;

        const pinPath = 'M22 13.8503C22 21.4458 13 27.9562 13 27.9562C13 27.9562 4 21.4458 4 13.8503C4 11.2603 4.94821 8.77641 6.63604 6.945C8.32387 5.11359 10.6131 4.08472 13 4.08472C15.3869 4.08472 17.6761 5.11359 19.364 6.945C21.0518 8.77641 22 11.2603 22 13.8503Z';

        const pinContent = isFinal
            ? `<path fill="none" stroke="${fillIconColor}" fill-rule="evenodd" clip-rule="evenodd" d="${pinPath}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="13" cy="13.8503" r="3.3" fill="${fillIconColor}" stroke="${fillIconColor}" stroke-width="2"/>
        <path d="M11.4 13.9L12.5 15L14.8 12.7" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`
            : `<path fill="${fillIconColor}" stroke="${fillIconColor}" fill-rule="evenodd" clip-rule="evenodd" d="${pinPath}" stroke-width="1.6276" stroke-linecap="round" stroke-linejoin="round"/>
        ${centerText
                ? `<text x="13" y="15" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="white">${centerText}</text>`
                : ''}`;

        return `<svg viewBox="0 -14 44 62" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g transform="translate(9, 3)" opacity="${opacity}">
    <g transform="translate(13, 27.956) scale(${pinScale}) translate(-13, -27.956)">
      ${pinContent}
    </g>
  </g>
  <rect x="8" y="${timeLabelY}" width="28" height="14" rx="7" fill="white" stroke="#1A1A1A" stroke-width="0.8"/>
  <text x="22" y="${timeLabelY + 7}" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="9" font-weight="600" fill="#1A1A1A">${timeText}</text>
  <circle cx="33" cy="${clockCy}" r="8.25" fill="white"/>
  <circle cx="33" cy="${clockCy}" r="7" fill="white" stroke="${fillIconColor}" stroke-width="1.5"/>
  <circle cx="33" cy="${clockCy}" r="4.2" fill="none" stroke="#1A1A1A" stroke-width="1"/>
  <path d="M33 ${clockCy}V${clockCy - 2.2}" stroke="#1A1A1A" stroke-width="1.2" stroke-linecap="round"/>
  <path d="M33 ${clockCy}H35.2" stroke="#1A1A1A" stroke-width="1.2" stroke-linecap="round"/>
</svg>`;
    }

    buildIconHrefTrack(track: UserGeoTrackDto): string {
        let svgContent = `<svg viewBox="0 0 120 56" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path
    d="M14 35.5C14 31.9101 16.9101 29 20.5 29H25.2L34.2 18.8C36.7 15.96 40.3 14.33 44.1 14.33H72.8C77.2 14.33 81.39 16.15 84.39 19.37L92.4 28H101.5C105.09 28 108 30.91 108 34.5V39.5C108 42.2614 105.761 44.5 103 44.5H98.6C97.68 48.97 93.72 52.33 89 52.33C84.28 52.33 80.32 48.97 79.4 44.5H40.6C39.68 48.97 35.72 52.33 31 52.33C26.28 52.33 22.32 48.97 21.4 44.5H19C16.239 44.5 14 42.2614 14 39.5V35.5Z"
    fill="${track.colorCode}"
  />

  <path
    d="M38.2 20.5C39.8 18.68 42.1 17.63 44.52 17.63H71.2C74.44 17.63 77.54 18.95 79.76 21.29L85.7 27.5H31.95L38.2 20.5Z"
    fill="white"
    fill-opacity="0.2"
  />

  <path
    d="M43.8 18.9H56.8V27.5H35.9L40.7 22.1C41.48 21.22 42.6 18.9 43.8 18.9Z"
    fill="white"
    fill-opacity="0.28"
  />

  <path
    d="M58.8 18.9H70.8C73.3 18.9 75.7 19.92 77.42 21.74L82.9 27.5H58.8V18.9Z"
    fill="white"
    fill-opacity="0.28"
  />

  <path
    d="M25.2 29H93.1"
    stroke="white"
    stroke-opacity="0.18"
    stroke-width="1.5"
    stroke-linecap="round"
  />

  <circle cx="31" cy="44" r="7.5" fill="white" />
  <circle cx="89" cy="44" r="7.5" fill="white" />

  <circle cx="31" cy="44" r="3.4" fill="${track.colorCode}" />
  <circle cx="89" cy="44" r="3.4" fill="${track.colorCode}" />
</svg>`;

        return `data:image/svg+xml;base64,${btoa(svgContent)}`;
    }

    buildIconHref(request: PlannedRequestDto): string {
        if (this.hasSpecificPlanTime(request)) {
            return this.buildTimedIconHref(request);
        }

        const hasPerformerUser = request.requestPerformerUserId != null && request.requestPerformerUserId != 0;
        const fillIconColor = hasPerformerUser && !this.isEmpty(request.requestPerformerUserColor)
            ? request.requestPerformerUserColor
            : 'black';

        var svgContent = '';

        if (hasPerformerUser) {
            if (request.isFinal) {
                svgContent = `
                    <svg viewBox="0 0 22 27" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M20.9002 10.8504C20.9002 18.4459 11.0002 24.9563 11.0002 24.9563C11.0002 24.9563 1.10016 18.4459 1.10016 10.8504C1.10016 8.26043 2.14319 5.7765 3.9998 3.94509C5.85641 2.11368 8.37452 1.08481 11.0002 1.08481C13.6258 1.08481 16.1439 2.11368 18.0005 3.94509C19.8571 5.7765 20.9002 8.26043 20.9002 10.8504Z"
    stroke="${fillIconColor}"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
  <circle
    cx="11"
    cy="10.8503"
    r="3.3"
    fill="${fillIconColor}"
    stroke="${fillIconColor}"
    stroke-width="2"
  />
  <path
    d="M9.4 10.9L10.5 12L12.8 9.7"
    stroke="white"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;
            }
            else if (request.mapPointState == MapPointStatesEnum.default || request.mapPointState == MapPointStatesEnum.disabled) {
                const opacity = request.mapPointState == MapPointStatesEnum.default ? 1 : this.defaultMapPointOpacity;
                svgContent = `
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                        <path fill="${fillIconColor}" fill-opacity="${opacity}" d="M128 252.6C128 148.4 214 64 320 64C426 64 512 148.4 512 252.6C512 371.9 391.8 514.9 341.6 569.4C329.8 582.2 310.1 582.2 298.3 569.4C248.1 514.9 127.9 371.9 127.9 252.6zM320 320C355.3 320 384 291.3 384 256C384 220.7 355.3 192 320 192C284.7 192 256 220.7 256 256C256 291.3 284.7 320 320 320z"/>
                    </svg>`;
            }
            else if (request.mapPointState == MapPointStatesEnum.enabled) {
                const text = request.planOrderNumber ? request.planOrderNumber.toString() : '';
                svgContent = `
                    <svg viewBox="0 0 26 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill="${fillIconColor}" fill-rule="evenodd" clip-rule="evenodd" d="M26 14.1059C26 25.0772 13 34.4811 13 34.4811C13 34.4811 0 25.0772 0 14.1059C0 10.3648 1.36964 6.77689 3.80761 4.13152C6.24558 1.48615 9.55219 0 13 0C16.4478 0 19.7544 1.48615 22.1924 4.13152C24.6304 6.77689 26 10.3648 26 14.1059Z" fill-opacity="0.43"/>
                        <path fill="${fillIconColor}" stroke="${fillIconColor}" fill-rule="evenodd" clip-rule="evenodd" d="M22 13.8503C22 21.4458 13 27.9562 13 27.9562C13 27.9562 4 21.4458 4 13.8503C4 11.2603 4.94821 8.77641 6.63604 6.945C8.32387 5.11359 10.6131 4.08472 13 4.08472C15.3869 4.08472 17.6761 5.11359 19.364 6.945C21.0518 8.77641 22 11.2603 22 13.8503Z" stroke-width="1.6276" stroke-linecap="round" stroke-linejoin="round"/>
                        <text x="13" y="15" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="white">
                            ${text}
                        </text>
                    </svg>`;
            }
        }
        else {
            if (request.mapPointState == MapPointStatesEnum.disabled) {
                svgContent = `
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                        <path fill="#465BFF" fill-opacity="${this.defaultMapPointOpacity}" d="M128 252.6C128 148.4 214 64 320 64C426 64 512 148.4 512 252.6C512 371.9 391.8 514.9 341.6 569.4C329.8 582.2 310.1 582.2 298.3 569.4C248.1 514.9 127.9 371.9 127.9 252.6zM320 320C355.3 320 384 291.3 384 256C384 220.7 355.3 192 320 192C284.7 192 256 220.7 256 256C256 291.3 284.7 320 320 320z"/>
                    </svg>`;
            }
            else {
                if (request.isFinal) {
                    svgContent = `
                    <svg viewBox="0 0 22 27" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path
    fill-rule="evenodd"
    clip-rule="evenodd"
    d="M20.9002 10.8504C20.9002 18.4459 11.0002 24.9563 11.0002 24.9563C11.0002 24.9563 1.10016 18.4459 1.10016 10.8504C1.10016 8.26043 2.14319 5.7765 3.9998 3.94509C5.85641 2.11368 8.37452 1.08481 11.0002 1.08481C13.6258 1.08481 16.1439 2.11368 18.0005 3.94509C19.8571 5.7765 20.9002 8.26043 20.9002 10.8504Z"
    stroke="#465BFF"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
  <circle
    cx="11"
    cy="10.8503"
    r="3.3"
    fill="#465BFF"
    stroke="#465BFF"
    stroke-width="2"
  />
  <path
    d="M9.4 10.9L10.5 12L12.8 9.7"
    stroke="white"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;
                } else {
                    svgContent = `
                        <svg viewBox="0 0 22 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M20.9002 10.8504C20.9002 18.4459 11.0002 24.9563 11.0002 24.9563C11.0002 24.9563 1.10016 18.4459 1.10016 10.8504C1.10016 8.26043 2.14319 5.7765 3.9998 3.94509C5.85641 2.11368 8.37452 1.08481 11.0002 1.08481C13.6258 1.08481 16.1439 2.11368 18.0005 3.94509C19.8571 5.7765 20.9002 8.26043 20.9002 10.8504Z" stroke="#465BFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M10.9998 14.1055C12.8224 14.1055 14.2998 12.6481 14.2998 10.8503C14.2998 9.05247 12.8224 7.59506 10.9998 7.59506C9.17729 7.59506 7.69983 9.05247 7.69983 10.8503C7.69983 12.6481 9.17729 14.1055 10.9998 14.1055Z" fill="#D9D9D9" stroke="#465BFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>`
                }
            }
        }

        return `data:image/svg+xml;base64,${btoa(svgContent)}`;
    }

    buildModalContent(request: PlannedRequestDto): string {
        const hasPerformerUser = request.requestPerformerUserId != null && request.requestPerformerUserId != 0;
        const fullReadonly = this.securityService.isSpecialRightClaimValid('FullReadonly');
        const iconsColor = hasPerformerUser ? request.requestPerformerUserColor : '#465bff';
        const assignPerformerContent = fullReadonly ? '' : `
                        <span class="btn-hover" style="margin-left: 0.25rem; color: ${iconsColor}; opacity: 0.8;"
                            onclick="window.${this.planningMapComponentName}.setRequestPerformerUser(${request.requestId})">
                            Запланировать исполнителя
                        </span>`;

        const titleContent = `
            <div style="display: flex; flex-direction: row; justify-content: space-between; margin-top: 0.5rem">
                <div style="display: flex; flex-direction: row">
                    <span style="font-size: 0.875rem; font-weight: 500;">№${request.requestId}</span>
                    <span class="btn-silver-hover" style="margin-left: 0.35rem;"
                        onclick="window.${this.planningMapComponentName}.openRequest(${request.requestId})">
                        <i class="fas fa-external-link-alt"></i>
                    </span>
                </div>
                <div style="display: flex; flex-direction: row; align-items: center; text-align: center;">
                    <div style="width: 0.35rem; height: 0.35rem; border-radius: 50%; background-color: ${request.requestPerformerUserColor};"></div>
                    <div style="font-size: 0.8rem; margin-left: 0.5rem;">${request.requestTypeName}</div>
                </div>
            </div>`;

        const addressContent = `
            <div class="mt-2" style="display: flex; flex-direction: row; text-align: center;">
                <svg width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke="${iconsColor}" fill-rule="evenodd" clip-rule="evenodd" d="M11.6737 6.66669C11.6737 11.3334 6.1441 15.3334 6.1441 15.3334C6.1441 15.3334 0.614502 11.3334 0.614502 6.66669C0.614502 5.07539 1.19708 3.54926 2.23408 2.42405C3.27109 1.29883 4.67756 0.666687 6.1441 0.666687C7.61064 0.666687 9.01712 1.29883 10.0541 2.42405C11.0911 3.54926 11.6737 5.07539 11.6737 6.66669Z" stroke-opacity="0.6" stroke-linecap="round" stroke-linejoin="round"/>
                    <path stroke="${iconsColor}" d="M6.14398 8.66669C7.16195 8.66669 7.98718 7.77126 7.98718 6.66669C7.98718 5.56212 7.16195 4.66669 6.14398 4.66669C5.12601 4.66669 4.30078 5.56212 4.30078 6.66669C4.30078 7.77126 5.12601 8.66669 6.14398 8.66669Z" stroke-opacity="0.6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span style="margin-left: 0.25rem;">${request.fullAddress}</span>
            </div>`;

        let performerUserContent = "";

        if (hasPerformerUser) {
            if (request.isFinal) {
                performerUserContent = `<div style="display: flex; flex-direction: row; justify-content: space-between; margin-top: 0.2rem;">
                    <div style="display: flex; flex-direction: row; text-align: center;">
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#DA3275" d="M10.3363 14.0011C10.2243 14.0011 10.1148 13.9678 10.0217 13.9056C9.92856 13.8434 9.85598 13.7549 9.81313 13.6515C9.77027 13.548 9.75905 13.4341 9.7809 13.3243C9.80274 13.2144 9.85666 13.1135 9.93585 13.0343L12.7677 10.2024C12.8745 10.0993 13.0176 10.0422 13.1661 10.0435C13.3146 10.0448 13.4566 10.1043 13.5617 10.2093C13.6667 10.3144 13.7262 10.4564 13.7275 10.6049C13.7288 10.7534 13.6717 10.8965 13.5686 11.0033L10.7367 13.8352C10.6842 13.8879 10.6218 13.9297 10.5531 13.9582C10.4843 13.9866 10.4107 14.0012 10.3363 14.0011Z" />
                            <path fill="#DA3275" d="M13.1613 14.0011C13.0869 14.0012 13.0132 13.9866 12.9445 13.9582C12.8758 13.9297 12.8134 13.8879 12.7609 13.8352L9.92901 11.0033C9.82584 10.8965 9.76875 10.7534 9.77004 10.6049C9.77133 10.4564 9.8309 10.3144 9.93591 10.2093C10.0409 10.1043 10.183 10.0448 10.3315 10.0435C10.48 10.0422 10.623 10.0993 10.7299 10.2024L13.5617 13.0343C13.6409 13.1135 13.6948 13.2144 13.7167 13.3243C13.7385 13.4341 13.7273 13.548 13.6844 13.6515C13.6416 13.7549 13.569 13.8434 13.4759 13.9056C13.3828 13.9678 13.2733 14.0011 13.1613 14.0011Z" />
                            <path fill="${iconsColor}" d="M11.7522 16.2734C10.9121 16.2734 10.0908 16.0243 9.39224 15.5575C8.69369 15.0908 8.14925 14.4274 7.82774 13.6512C7.50624 12.875 7.42211 12.0209 7.58602 11.1969C7.74992 10.3729 8.15448 9.61605 8.74854 9.02198C9.34261 8.42792 10.0995 8.02336 10.9235 7.85945C11.7475 7.69555 12.6016 7.77967 13.3777 8.10118C14.1539 8.42268 14.8173 8.96713 15.2841 9.66568C15.7508 10.3642 16 11.1855 16 12.0256C15.9987 13.1518 15.5508 14.2315 14.7544 15.0279C13.9581 15.8242 12.8784 16.2721 11.7522 16.2734ZM11.7522 8.91058C11.1361 8.91058 10.5338 9.09327 10.0216 9.43556C9.50929 9.77784 9.11003 10.2643 8.87426 10.8335C8.63849 11.4027 8.5768 12.0291 8.69699 12.6333C8.81719 13.2376 9.11387 13.7926 9.54951 14.2283C9.98516 14.6639 10.5402 14.9606 11.1445 15.0808C11.7487 15.201 12.3751 15.1393 12.9443 14.9035C13.5135 14.6678 14 14.2685 14.3422 13.7562C14.6845 13.244 14.8672 12.6417 14.8672 12.0256C14.8663 11.1998 14.5377 10.408 13.9538 9.82403C13.3698 9.24006 12.578 8.91155 11.7522 8.91058Z" fill-opacity="0.6"/>
                            <path fill="${iconsColor}" d="M0.566372 14.575C0.416161 14.575 0.272102 14.5153 0.165886 14.4091C0.0596711 14.3029 0 14.1588 0 14.0086C0.00384296 12.6901 0.412637 11.4046 1.17107 10.3261C1.92949 9.24757 3.00094 8.42806 4.24042 7.97847C3.57071 7.41444 3.10656 6.64457 2.92039 5.78901C2.73422 4.93344 2.83652 4.04032 3.21131 3.24901C3.5861 2.45769 4.21231 1.81271 4.99221 1.41471C5.77211 1.01671 6.66182 0.888078 7.52251 1.04889C8.3832 1.20971 9.16644 1.65092 9.75001 2.30368C10.3336 2.95644 10.6846 3.78402 10.7484 4.65728C10.8122 5.53054 10.5851 6.40034 10.1025 7.13096C9.62001 7.86158 8.90918 8.41191 8.08099 8.69606C8.01059 8.72007 7.93616 8.72997 7.86193 8.72521C7.7877 8.72045 7.71514 8.70111 7.64839 8.66831C7.51358 8.60206 7.4106 8.48496 7.36212 8.34279C7.31364 8.20061 7.32363 8.045 7.38988 7.91019C7.45613 7.77538 7.57322 7.6724 7.7154 7.62392C8.34942 7.40641 8.88574 6.97105 9.22895 6.39528C9.57216 5.81951 9.7 5.14066 9.58974 4.4795C9.47949 3.81833 9.13827 3.2177 8.6268 2.78446C8.11533 2.35122 7.46676 2.11345 6.79646 2.11345C6.12616 2.11345 5.47759 2.35122 4.96612 2.78446C4.45465 3.2177 4.11343 3.81833 4.00317 4.4795C3.89292 5.14066 4.02076 5.81951 4.36397 6.39528C4.70718 6.97105 5.24349 7.40641 5.87752 7.62392C5.99639 7.66446 6.09841 7.74335 6.16755 7.8482C6.23668 7.95304 6.26901 8.07789 6.25944 8.20312C6.24988 8.32834 6.19896 8.44683 6.11471 8.53996C6.03045 8.63309 5.91763 8.69557 5.79398 8.71759C3.00602 9.21429 1.13274 11.3405 1.13274 14.0086C1.13274 14.1588 1.07307 14.3029 0.966857 14.4091C0.860642 14.5153 0.716583 14.575 0.566372 14.575Z" fill-opacity="0.6"/>
                        </svg>
                        <span style="margin-left: 0.25rem;">${request.requestPerformerUserName}</span>
                    </div>
                    <span class="btn-silver-hover" style="font-size: 0.8rem; margin-left: 2.5rem;"
                        onclick="window.${this.planningMapComponentName}.showAllPerformerRequests(${request.requestPerformerUserId})"
                        >Все заявки</span>
                </div>`;
            } else {
                performerUserContent = `<div style="display: flex; flex-direction: row; justify-content: space-between; margin-top: 0.2rem;">
                    <div style="display: flex; flex-direction: row; text-align: center;">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill="${iconsColor}" d="M11.7522 15.292C10.9121 15.292 10.0908 15.0429 9.39224 14.5761C8.69369 14.1094 8.14925 13.446 7.82774 12.6698C7.50624 11.8936 7.42211 11.0395 7.58602 10.2155C7.74992 9.39154 8.15448 8.63466 8.74854 8.0406C9.34261 7.44653 10.0995 7.04197 10.9235 6.87807C11.7475 6.71417 12.6016 6.79829 13.3777 7.11979C14.1539 7.4413 14.8173 7.98575 15.2841 8.68429C15.7508 9.38284 16 10.2041 16 11.0442C15.9987 12.1704 15.5508 13.2501 14.7544 14.0465C13.9581 14.8428 12.8784 15.2907 11.7522 15.292ZM11.7522 7.92919C11.1361 7.92919 10.5338 8.11189 10.0216 8.45417C9.50929 8.79646 9.11003 9.28296 8.87426 9.85216C8.63849 10.4214 8.5768 11.0477 8.69699 11.652C8.81719 12.2562 9.11387 12.8113 9.54951 13.2469C9.98516 13.6826 10.5402 13.9792 11.1445 14.0994C11.7487 14.2196 12.3751 14.1579 12.9443 13.9222C13.5135 13.6864 14 13.2871 14.3422 12.7749C14.6845 12.2626 14.8672 11.6603 14.8672 11.0442C14.8663 10.2184 14.5377 9.42662 13.9538 8.84264C13.3698 8.25867 12.578 7.93017 11.7522 7.92919Z" fill-opacity="0.6"/>
                            <path stroke="${iconsColor}" d="M10.9272 6.89508C11.7476 6.73198 12.5978 6.81626 13.3706 7.13629C14.1434 7.45641 14.8042 7.99842 15.269 8.69391C15.7339 9.38954 15.9819 10.2079 15.9819 11.0445C15.9806 12.1659 15.5347 13.2408 14.7417 14.0338C13.9487 14.8267 12.8738 15.2726 11.7524 15.274C10.9158 15.274 10.0975 15.0259 9.40186 14.5611C8.70637 14.0963 8.16436 13.4355 7.84424 12.6627C7.52421 11.8899 7.43992 11.0397 7.60303 10.2193C7.76625 9.39874 8.16964 8.64487 8.76123 8.05328C9.35282 7.4617 10.1067 7.0583 10.9272 6.89508ZM11.7524 7.91168C11.1331 7.91168 10.5273 8.09502 10.0122 8.43903C9.49706 8.78324 9.09503 9.27288 8.85791 9.84528C8.6208 10.4177 8.55832 11.0481 8.6792 11.6558C8.8001 12.2633 9.0987 12.8213 9.53662 13.2593C9.97474 13.6975 10.5334 13.9959 11.1411 14.1168C11.7487 14.2376 12.3783 14.1751 12.9507 13.938C13.5231 13.7009 14.0127 13.2999 14.3569 12.7847C14.7011 12.2696 14.8852 11.664 14.8853 11.0445C14.8843 10.2139 14.5536 9.41694 13.9663 8.82965C13.3791 8.24256 12.5828 7.91272 11.7524 7.91168Z" stroke-opacity="0.6" stroke-width="0.0353982"/>
                            <path fill="${iconsColor}" d="M0.566372 13.5936C0.416161 13.5936 0.272102 13.5339 0.165886 13.4277C0.0596711 13.3215 0 13.1774 0 13.0272C0.00384296 11.7087 0.412637 10.4232 1.17107 9.34466C1.92949 8.26613 3.00094 7.44662 4.24042 6.99703C3.57071 6.43299 3.10656 5.66312 2.92039 4.80756C2.73422 3.952 2.83652 3.05887 3.21131 2.26756C3.5861 1.47625 4.21231 0.831265 4.99221 0.433264C5.77211 0.0352622 6.66182 -0.0933672 7.52251 0.0674486C8.3832 0.228264 9.16644 0.669478 9.75001 1.32224C10.3336 1.975 10.6846 2.80258 10.7484 3.67584C10.8122 4.54909 10.5851 5.4189 10.1025 6.14952C9.62001 6.88013 8.90918 7.43046 8.08099 7.71462C8.01059 7.73862 7.93616 7.74853 7.86193 7.74377C7.7877 7.739 7.71514 7.71967 7.64839 7.68686C7.51358 7.62061 7.4106 7.50352 7.36212 7.36134C7.31364 7.21917 7.32363 7.06356 7.38988 6.92875C7.45613 6.79393 7.57322 6.69096 7.7154 6.64248C8.34942 6.42496 8.88574 5.9896 9.22895 5.41383C9.57216 4.83806 9.7 4.15922 9.58974 3.49805C9.47949 2.83688 9.13827 2.23626 8.6268 1.80301C8.11533 1.36977 7.46676 1.13201 6.79646 1.13201C6.12616 1.13201 5.47759 1.36977 4.96612 1.80301C4.45465 2.23626 4.11343 2.83688 4.00317 3.49805C3.89292 4.15922 4.02076 4.83806 4.36397 5.41383C4.70718 5.9896 5.24349 6.42496 5.87752 6.64248C5.99639 6.68301 6.09841 6.76191 6.16755 6.86675C6.23668 6.9716 6.26901 7.09645 6.25944 7.22167C6.24988 7.3469 6.19896 7.46539 6.11471 7.55852C6.03045 7.65165 5.91763 7.71413 5.79398 7.73614C3.00602 8.23285 1.13274 10.359 1.13274 13.0272C1.13274 13.1774 1.07307 13.3215 0.966857 13.4277C0.860642 13.5339 0.716583 13.5936 0.566372 13.5936Z" fill-opacity="0.6"/>
                            <path stroke="${iconsColor}" d="M5 0.449219C5.77642 0.0529939 6.66269 -0.0751369 7.51953 0.0849609C8.37615 0.245081 9.15545 0.684358 9.73633 1.33398C10.3172 1.98373 10.6669 2.80754 10.7305 3.67676C10.794 4.54606 10.5682 5.41232 10.0879 6.13965C9.60751 6.867 8.89969 7.41535 8.0752 7.69824C8.00713 7.72142 7.93504 7.73113 7.86328 7.72656C7.79138 7.72195 7.72091 7.70268 7.65625 7.6709C7.52565 7.60672 7.42587 7.4932 7.37891 7.35547C7.33199 7.21777 7.34209 7.06708 7.40625 6.93652C7.4704 6.80614 7.5832 6.70617 7.7207 6.65918C8.35869 6.44031 8.89879 6.00222 9.24414 5.42285C9.58948 4.84349 9.71836 4.1604 9.60742 3.49512C9.49648 2.82983 9.15332 2.22501 8.63867 1.78906C8.12411 1.3532 7.47122 1.11436 6.79688 1.11426C6.12258 1.11426 5.46968 1.35336 4.95508 1.78906C4.44041 2.22501 4.0963 2.82982 3.98535 3.49512C3.87442 4.16037 4.00333 4.84351 4.34863 5.42285C4.69399 6.00222 5.23408 6.44031 5.87207 6.65918C5.98713 6.69847 6.08541 6.77545 6.15234 6.87695C6.21926 6.9785 6.25145 7.09944 6.24219 7.2207C6.23285 7.34189 6.18311 7.45674 6.10156 7.54688C6.01998 7.63699 5.91069 7.69742 5.79102 7.71875C2.99537 8.21683 1.11523 10.3498 1.11523 13.0273C1.11519 13.1728 1.05696 13.3122 0.954102 13.415C0.85122 13.5179 0.711881 13.5762 0.566406 13.5762C0.420935 13.5762 0.281598 13.5179 0.178711 13.415C0.0758527 13.3122 0.0176205 13.1728 0.0175781 13.0273C0.0214105 11.7125 0.429205 10.4301 1.18555 9.35449C1.94181 8.27914 3.01025 7.46202 4.24609 7.01367L4.27539 7.00293L4.25195 6.9834C3.58523 6.42188 3.12284 5.65545 2.9375 4.80371C2.75221 3.95205 2.85448 3.0631 3.22754 2.27539C3.60061 1.4877 4.22371 0.845455 5 0.449219Z" stroke-opacity="0.6" stroke-width="0.0353982"/>
                            <path stroke="${iconsColor}" d="M9.91162 11.7522L11.0698 12.824C11.0848 12.8378 11.1084 12.8362 11.1213 12.8204L13.6107 9.7699" stroke-opacity="0.6" stroke-width="1.13274" stroke-linecap="round"/>
                        </svg>
                        <span style="margin-left: 0.25rem;">${request.requestPerformerUserName}</span>
                    </div>
                    <span class="btn-silver-hover" style="font-size: 0.8rem; margin-left: 2.5rem;"
                        onclick="window.${this.planningMapComponentName}.showAllPerformerRequests(${request.requestPerformerUserId})"
                        >Все заявки</span>
                </div>`;
            }
        } else {
            if (request.isFinal) {
                performerUserContent = `<div style="display: flex; flex-direction: row; justify-content: space-between; margin-top: 0.2rem;">
                    <div style="display: flex; flex-direction: row; text-align: center;">
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#DA3275" d="M10.3363 14.0011C10.2243 14.0011 10.1148 13.9678 10.0217 13.9056C9.92856 13.8434 9.85598 13.7549 9.81313 13.6515C9.77027 13.548 9.75905 13.4341 9.7809 13.3243C9.80274 13.2144 9.85666 13.1135 9.93585 13.0343L12.7677 10.2024C12.8745 10.0993 13.0176 10.0422 13.1661 10.0435C13.3146 10.0448 13.4566 10.1043 13.5617 10.2093C13.6667 10.3144 13.7262 10.4564 13.7275 10.6049C13.7288 10.7534 13.6717 10.8965 13.5686 11.0033L10.7367 13.8352C10.6842 13.8879 10.6218 13.9297 10.5531 13.9582C10.4843 13.9866 10.4107 14.0012 10.3363 14.0011Z" />
                            <path fill="#DA3275" d="M13.1613 14.0011C13.0869 14.0012 13.0132 13.9866 12.9445 13.9582C12.8758 13.9297 12.8134 13.8879 12.7609 13.8352L9.92901 11.0033C9.82584 10.8965 9.76875 10.7534 9.77004 10.6049C9.77133 10.4564 9.8309 10.3144 9.93591 10.2093C10.0409 10.1043 10.183 10.0448 10.3315 10.0435C10.48 10.0422 10.623 10.0993 10.7299 10.2024L13.5617 13.0343C13.6409 13.1135 13.6948 13.2144 13.7167 13.3243C13.7385 13.4341 13.7273 13.548 13.6844 13.6515C13.6416 13.7549 13.569 13.8434 13.4759 13.9056C13.3828 13.9678 13.2733 14.0011 13.1613 14.0011Z" />
                            <path fill="${iconsColor}" d="M11.7522 16.2734C10.9121 16.2734 10.0908 16.0243 9.39224 15.5575C8.69369 15.0908 8.14925 14.4274 7.82774 13.6512C7.50624 12.875 7.42211 12.0209 7.58602 11.1969C7.74992 10.3729 8.15448 9.61605 8.74854 9.02198C9.34261 8.42792 10.0995 8.02336 10.9235 7.85945C11.7475 7.69555 12.6016 7.77967 13.3777 8.10118C14.1539 8.42268 14.8173 8.96713 15.2841 9.66568C15.7508 10.3642 16 11.1855 16 12.0256C15.9987 13.1518 15.5508 14.2315 14.7544 15.0279C13.9581 15.8242 12.8784 16.2721 11.7522 16.2734ZM11.7522 8.91058C11.1361 8.91058 10.5338 9.09327 10.0216 9.43556C9.50929 9.77784 9.11003 10.2643 8.87426 10.8335C8.63849 11.4027 8.5768 12.0291 8.69699 12.6333C8.81719 13.2376 9.11387 13.7926 9.54951 14.2283C9.98516 14.6639 10.5402 14.9606 11.1445 15.0808C11.7487 15.201 12.3751 15.1393 12.9443 14.9035C13.5135 14.6678 14 14.2685 14.3422 13.7562C14.6845 13.244 14.8672 12.6417 14.8672 12.0256C14.8663 11.1998 14.5377 10.408 13.9538 9.82403C13.3698 9.24006 12.578 8.91155 11.7522 8.91058Z" fill-opacity="0.6"/>
                            <path fill="${iconsColor}" d="M0.566372 14.575C0.416161 14.575 0.272102 14.5153 0.165886 14.4091C0.0596711 14.3029 0 14.1588 0 14.0086C0.00384296 12.6901 0.412637 11.4046 1.17107 10.3261C1.92949 9.24757 3.00094 8.42806 4.24042 7.97847C3.57071 7.41444 3.10656 6.64457 2.92039 5.78901C2.73422 4.93344 2.83652 4.04032 3.21131 3.24901C3.5861 2.45769 4.21231 1.81271 4.99221 1.41471C5.77211 1.01671 6.66182 0.888078 7.52251 1.04889C8.3832 1.20971 9.16644 1.65092 9.75001 2.30368C10.3336 2.95644 10.6846 3.78402 10.7484 4.65728C10.8122 5.53054 10.5851 6.40034 10.1025 7.13096C9.62001 7.86158 8.90918 8.41191 8.08099 8.69606C8.01059 8.72007 7.93616 8.72997 7.86193 8.72521C7.7877 8.72045 7.71514 8.70111 7.64839 8.66831C7.51358 8.60206 7.4106 8.48496 7.36212 8.34279C7.31364 8.20061 7.32363 8.045 7.38988 7.91019C7.45613 7.77538 7.57322 7.6724 7.7154 7.62392C8.34942 7.40641 8.88574 6.97105 9.22895 6.39528C9.57216 5.81951 9.7 5.14066 9.58974 4.4795C9.47949 3.81833 9.13827 3.2177 8.6268 2.78446C8.11533 2.35122 7.46676 2.11345 6.79646 2.11345C6.12616 2.11345 5.47759 2.35122 4.96612 2.78446C4.45465 3.2177 4.11343 3.81833 4.00317 4.4795C3.89292 5.14066 4.02076 5.81951 4.36397 6.39528C4.70718 6.97105 5.24349 7.40641 5.87752 7.62392C5.99639 7.66446 6.09841 7.74335 6.16755 7.8482C6.23668 7.95304 6.26901 8.07789 6.25944 8.20312C6.24988 8.32834 6.19896 8.44683 6.11471 8.53996C6.03045 8.63309 5.91763 8.69557 5.79398 8.71759C3.00602 9.21429 1.13274 11.3405 1.13274 14.0086C1.13274 14.1588 1.07307 14.3029 0.966857 14.4091C0.860642 14.5153 0.716583 14.575 0.566372 14.575Z" fill-opacity="0.6"/>
                        </svg>
                    </div>
                </div>`;
            } else {
                performerUserContent = `<div style="display: flex; flex-direction: row; justify-content: space-between; margin-top: 0.2rem;">
                    <div style="display: flex; flex-direction: row; text-align: center;">
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#DA3275" d="M10.3363 14.0011C10.2243 14.0011 10.1148 13.9678 10.0217 13.9056C9.92856 13.8434 9.85598 13.7549 9.81313 13.6515C9.77027 13.548 9.75905 13.4341 9.7809 13.3243C9.80274 13.2144 9.85666 13.1135 9.93585 13.0343L12.7677 10.2024C12.8745 10.0993 13.0176 10.0422 13.1661 10.0435C13.3146 10.0448 13.4566 10.1043 13.5617 10.2093C13.6667 10.3144 13.7262 10.4564 13.7275 10.6049C13.7288 10.7534 13.6717 10.8965 13.5686 11.0033L10.7367 13.8352C10.6842 13.8879 10.6218 13.9297 10.5531 13.9582C10.4843 13.9866 10.4107 14.0012 10.3363 14.0011Z" />
                            <path fill="#DA3275" d="M13.1613 14.0011C13.0869 14.0012 13.0132 13.9866 12.9445 13.9582C12.8758 13.9297 12.8134 13.8879 12.7609 13.8352L9.92901 11.0033C9.82584 10.8965 9.76875 10.7534 9.77004 10.6049C9.77133 10.4564 9.8309 10.3144 9.93591 10.2093C10.0409 10.1043 10.183 10.0448 10.3315 10.0435C10.48 10.0422 10.623 10.0993 10.7299 10.2024L13.5617 13.0343C13.6409 13.1135 13.6948 13.2144 13.7167 13.3243C13.7385 13.4341 13.7273 13.548 13.6844 13.6515C13.6416 13.7549 13.569 13.8434 13.4759 13.9056C13.3828 13.9678 13.2733 14.0011 13.1613 14.0011Z" />
                            <path fill="${iconsColor}" d="M11.7522 16.2734C10.9121 16.2734 10.0908 16.0243 9.39224 15.5575C8.69369 15.0908 8.14925 14.4274 7.82774 13.6512C7.50624 12.875 7.42211 12.0209 7.58602 11.1969C7.74992 10.3729 8.15448 9.61605 8.74854 9.02198C9.34261 8.42792 10.0995 8.02336 10.9235 7.85945C11.7475 7.69555 12.6016 7.77967 13.3777 8.10118C14.1539 8.42268 14.8173 8.96713 15.2841 9.66568C15.7508 10.3642 16 11.1855 16 12.0256C15.9987 13.1518 15.5508 14.2315 14.7544 15.0279C13.9581 15.8242 12.8784 16.2721 11.7522 16.2734ZM11.7522 8.91058C11.1361 8.91058 10.5338 9.09327 10.0216 9.43556C9.50929 9.77784 9.11003 10.2643 8.87426 10.8335C8.63849 11.4027 8.5768 12.0291 8.69699 12.6333C8.81719 13.2376 9.11387 13.7926 9.54951 14.2283C9.98516 14.6639 10.5402 14.9606 11.1445 15.0808C11.7487 15.201 12.3751 15.1393 12.9443 14.9035C13.5135 14.6678 14 14.2685 14.3422 13.7562C14.6845 13.244 14.8672 12.6417 14.8672 12.0256C14.8663 11.1998 14.5377 10.408 13.9538 9.82403C13.3698 9.24006 12.578 8.91155 11.7522 8.91058Z" fill-opacity="0.6"/>
                            <path fill="${iconsColor}" d="M0.566372 14.575C0.416161 14.575 0.272102 14.5153 0.165886 14.4091C0.0596711 14.3029 0 14.1588 0 14.0086C0.00384296 12.6901 0.412637 11.4046 1.17107 10.3261C1.92949 9.24757 3.00094 8.42806 4.24042 7.97847C3.57071 7.41444 3.10656 6.64457 2.92039 5.78901C2.73422 4.93344 2.83652 4.04032 3.21131 3.24901C3.5861 2.45769 4.21231 1.81271 4.99221 1.41471C5.77211 1.01671 6.66182 0.888078 7.52251 1.04889C8.3832 1.20971 9.16644 1.65092 9.75001 2.30368C10.3336 2.95644 10.6846 3.78402 10.7484 4.65728C10.8122 5.53054 10.5851 6.40034 10.1025 7.13096C9.62001 7.86158 8.90918 8.41191 8.08099 8.69606C8.01059 8.72007 7.93616 8.72997 7.86193 8.72521C7.7877 8.72045 7.71514 8.70111 7.64839 8.66831C7.51358 8.60206 7.4106 8.48496 7.36212 8.34279C7.31364 8.20061 7.32363 8.045 7.38988 7.91019C7.45613 7.77538 7.57322 7.6724 7.7154 7.62392C8.34942 7.40641 8.88574 6.97105 9.22895 6.39528C9.57216 5.81951 9.7 5.14066 9.58974 4.4795C9.47949 3.81833 9.13827 3.2177 8.6268 2.78446C8.11533 2.35122 7.46676 2.11345 6.79646 2.11345C6.12616 2.11345 5.47759 2.35122 4.96612 2.78446C4.45465 3.2177 4.11343 3.81833 4.00317 4.4795C3.89292 5.14066 4.02076 5.81951 4.36397 6.39528C4.70718 6.97105 5.24349 7.40641 5.87752 7.62392C5.99639 7.66446 6.09841 7.74335 6.16755 7.8482C6.23668 7.95304 6.26901 8.07789 6.25944 8.20312C6.24988 8.32834 6.19896 8.44683 6.11471 8.53996C6.03045 8.63309 5.91763 8.69557 5.79398 8.71759C3.00602 9.21429 1.13274 11.3405 1.13274 14.0086C1.13274 14.1588 1.07307 14.3029 0.966857 14.4091C0.860642 14.5153 0.716583 14.575 0.566372 14.575Z" fill-opacity="0.6"/>
                        </svg>
                        ${assignPerformerContent}
                    </div>
                </div>`;
            }
        }

        const planTimeContent = request.overallPlanTime ? `
            <div style="display: flex; flex-direction: row; margin-top: 0.2rem;">
                <svg width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- Adapted hourglass from the provided SVG -->
            <path
                d="M1.6 1.8H11.4
                M2.4 1.8V3.4C2.4 4.25 2.8 5.05 3.45 5.55L5.95 7.45
                L3.55 9.3C2.85 9.85 2.45 10.7 2.45 11.6V13.2
                M10.6 1.8V3.4C10.6 4.25 10.2 5.05 9.55 5.55L7.05 7.45
                L9.45 9.3C10.15 9.85 10.55 10.7 10.55 11.6V13.2
                M1.6 14.2H11.4"
                stroke="${iconsColor}"
                stroke-opacity="0.6"
                stroke-width="1.2"
                stroke-linecap="round"
                stroke-linejoin="round"
            />

            <!-- inner "sand" hint -->
            <path
                d="M4.1 4.2H8.9
                M6.5 7.45L6.5 8.55
                M4.2 11.8H8.8"
                stroke="${iconsColor}"
                stroke-opacity="0.6"
                stroke-width="1.2"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
            </svg>
                <span style="margin-left: 0.25rem;">${request.overallPlanTime}</span>
            </div>` : ``;

        const buttonContent = hasPerformerUser && !request.isFinal && !fullReadonly
            ? `<div class="mt-3">
                    <button class="btn bwt-btn-primary" style="padding: 0.1rem, 0.2rem; font-size: 0.75rem; font-weight: 500;"
                    onclick="window.${this.planningMapComponentName}.resetRequestPerformerUser(${request.requestId})">
                        <i class="fas fa-user">&nbsp;&nbsp;</i>Переназначить</button>
                </div>`
            : '';

        return `
            <div style="padding: 0.25rem;">
                ${titleContent}
                ${addressContent}
                ${performerUserContent}
                ${planTimeContent}
                ${buttonContent}
            </div>`;
    }

    buildModalContentTrack(track: UserGeoTrackDto, lastPoint: UserGeoTrackPointDto): string {
        const localShiftMs = -new Date().getTimezoneOffset() * 60 * 1000;
        const time = new Date(new Date(lastPoint.deviceTimestampUtc).getTime() + localShiftMs).toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });

        const timeContent = `
            <div class="mt-2" style="display: flex; flex-direction: row; text-align: center;">
                <svg width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle
    cx="6.1441"
    cy="8"
    r="4.8"
    stroke="${track.colorCode}"
    stroke-opacity="0.6"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
  <path
    d="M6.14404 5.6V8L7.98615 9.33333"
    stroke="${track.colorCode}"
    stroke-opacity="0.6"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>
                <span style="margin-left: 0.25rem;">${time}</span>
            </div>`;


        const performerUserContent = `<div style="display: flex; flex-direction: row; justify-content: space-between; margin-top: 0.2rem;">
                    <div style="display: flex; flex-direction: row; text-align: center;">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill="${track.colorCode}" d="M11.7522 15.292C10.9121 15.292 10.0908 15.0429 9.39224 14.5761C8.69369 14.1094 8.14925 13.446 7.82774 12.6698C7.50624 11.8936 7.42211 11.0395 7.58602 10.2155C7.74992 9.39154 8.15448 8.63466 8.74854 8.0406C9.34261 7.44653 10.0995 7.04197 10.9235 6.87807C11.7475 6.71417 12.6016 6.79829 13.3777 7.11979C14.1539 7.4413 14.8173 7.98575 15.2841 8.68429C15.7508 9.38284 16 10.2041 16 11.0442C15.9987 12.1704 15.5508 13.2501 14.7544 14.0465C13.9581 14.8428 12.8784 15.2907 11.7522 15.292ZM11.7522 7.92919C11.1361 7.92919 10.5338 8.11189 10.0216 8.45417C9.50929 8.79646 9.11003 9.28296 8.87426 9.85216C8.63849 10.4214 8.5768 11.0477 8.69699 11.652C8.81719 12.2562 9.11387 12.8113 9.54951 13.2469C9.98516 13.6826 10.5402 13.9792 11.1445 14.0994C11.7487 14.2196 12.3751 14.1579 12.9443 13.9222C13.5135 13.6864 14 13.2871 14.3422 12.7749C14.6845 12.2626 14.8672 11.6603 14.8672 11.0442C14.8663 10.2184 14.5377 9.42662 13.9538 8.84264C13.3698 8.25867 12.578 7.93017 11.7522 7.92919Z" fill-opacity="0.6"/>
                            <path stroke="${track.colorCode}" d="M10.9272 6.89508C11.7476 6.73198 12.5978 6.81626 13.3706 7.13629C14.1434 7.45641 14.8042 7.99842 15.269 8.69391C15.7339 9.38954 15.9819 10.2079 15.9819 11.0445C15.9806 12.1659 15.5347 13.2408 14.7417 14.0338C13.9487 14.8267 12.8738 15.2726 11.7524 15.274C10.9158 15.274 10.0975 15.0259 9.40186 14.5611C8.70637 14.0963 8.16436 13.4355 7.84424 12.6627C7.52421 11.8899 7.43992 11.0397 7.60303 10.2193C7.76625 9.39874 8.16964 8.64487 8.76123 8.05328C9.35282 7.4617 10.1067 7.0583 10.9272 6.89508ZM11.7524 7.91168C11.1331 7.91168 10.5273 8.09502 10.0122 8.43903C9.49706 8.78324 9.09503 9.27288 8.85791 9.84528C8.6208 10.4177 8.55832 11.0481 8.6792 11.6558C8.8001 12.2633 9.0987 12.8213 9.53662 13.2593C9.97474 13.6975 10.5334 13.9959 11.1411 14.1168C11.7487 14.2376 12.3783 14.1751 12.9507 13.938C13.5231 13.7009 14.0127 13.2999 14.3569 12.7847C14.7011 12.2696 14.8852 11.664 14.8853 11.0445C14.8843 10.2139 14.5536 9.41694 13.9663 8.82965C13.3791 8.24256 12.5828 7.91272 11.7524 7.91168Z" stroke-opacity="0.6" stroke-width="0.0353982"/>
                            <path fill="${track.colorCode}" d="M0.566372 13.5936C0.416161 13.5936 0.272102 13.5339 0.165886 13.4277C0.0596711 13.3215 0 13.1774 0 13.0272C0.00384296 11.7087 0.412637 10.4232 1.17107 9.34466C1.92949 8.26613 3.00094 7.44662 4.24042 6.99703C3.57071 6.43299 3.10656 5.66312 2.92039 4.80756C2.73422 3.952 2.83652 3.05887 3.21131 2.26756C3.5861 1.47625 4.21231 0.831265 4.99221 0.433264C5.77211 0.0352622 6.66182 -0.0933672 7.52251 0.0674486C8.3832 0.228264 9.16644 0.669478 9.75001 1.32224C10.3336 1.975 10.6846 2.80258 10.7484 3.67584C10.8122 4.54909 10.5851 5.4189 10.1025 6.14952C9.62001 6.88013 8.90918 7.43046 8.08099 7.71462C8.01059 7.73862 7.93616 7.74853 7.86193 7.74377C7.7877 7.739 7.71514 7.71967 7.64839 7.68686C7.51358 7.62061 7.4106 7.50352 7.36212 7.36134C7.31364 7.21917 7.32363 7.06356 7.38988 6.92875C7.45613 6.79393 7.57322 6.69096 7.7154 6.64248C8.34942 6.42496 8.88574 5.9896 9.22895 5.41383C9.57216 4.83806 9.7 4.15922 9.58974 3.49805C9.47949 2.83688 9.13827 2.23626 8.6268 1.80301C8.11533 1.36977 7.46676 1.13201 6.79646 1.13201C6.12616 1.13201 5.47759 1.36977 4.96612 1.80301C4.45465 2.23626 4.11343 2.83688 4.00317 3.49805C3.89292 4.15922 4.02076 4.83806 4.36397 5.41383C4.70718 5.9896 5.24349 6.42496 5.87752 6.64248C5.99639 6.68301 6.09841 6.76191 6.16755 6.86675C6.23668 6.9716 6.26901 7.09645 6.25944 7.22167C6.24988 7.3469 6.19896 7.46539 6.11471 7.55852C6.03045 7.65165 5.91763 7.71413 5.79398 7.73614C3.00602 8.23285 1.13274 10.359 1.13274 13.0272C1.13274 13.1774 1.07307 13.3215 0.966857 13.4277C0.860642 13.5339 0.716583 13.5936 0.566372 13.5936Z" fill-opacity="0.6"/>
                            <path stroke="${track.colorCode}" d="M5 0.449219C5.77642 0.0529939 6.66269 -0.0751369 7.51953 0.0849609C8.37615 0.245081 9.15545 0.684358 9.73633 1.33398C10.3172 1.98373 10.6669 2.80754 10.7305 3.67676C10.794 4.54606 10.5682 5.41232 10.0879 6.13965C9.60751 6.867 8.89969 7.41535 8.0752 7.69824C8.00713 7.72142 7.93504 7.73113 7.86328 7.72656C7.79138 7.72195 7.72091 7.70268 7.65625 7.6709C7.52565 7.60672 7.42587 7.4932 7.37891 7.35547C7.33199 7.21777 7.34209 7.06708 7.40625 6.93652C7.4704 6.80614 7.5832 6.70617 7.7207 6.65918C8.35869 6.44031 8.89879 6.00222 9.24414 5.42285C9.58948 4.84349 9.71836 4.1604 9.60742 3.49512C9.49648 2.82983 9.15332 2.22501 8.63867 1.78906C8.12411 1.3532 7.47122 1.11436 6.79688 1.11426C6.12258 1.11426 5.46968 1.35336 4.95508 1.78906C4.44041 2.22501 4.0963 2.82982 3.98535 3.49512C3.87442 4.16037 4.00333 4.84351 4.34863 5.42285C4.69399 6.00222 5.23408 6.44031 5.87207 6.65918C5.98713 6.69847 6.08541 6.77545 6.15234 6.87695C6.21926 6.9785 6.25145 7.09944 6.24219 7.2207C6.23285 7.34189 6.18311 7.45674 6.10156 7.54688C6.01998 7.63699 5.91069 7.69742 5.79102 7.71875C2.99537 8.21683 1.11523 10.3498 1.11523 13.0273C1.11519 13.1728 1.05696 13.3122 0.954102 13.415C0.85122 13.5179 0.711881 13.5762 0.566406 13.5762C0.420935 13.5762 0.281598 13.5179 0.178711 13.415C0.0758527 13.3122 0.0176205 13.1728 0.0175781 13.0273C0.0214105 11.7125 0.429205 10.4301 1.18555 9.35449C1.94181 8.27914 3.01025 7.46202 4.24609 7.01367L4.27539 7.00293L4.25195 6.9834C3.58523 6.42188 3.12284 5.65545 2.9375 4.80371C2.75221 3.95205 2.85448 3.0631 3.22754 2.27539C3.60061 1.4877 4.22371 0.845455 5 0.449219Z" stroke-opacity="0.6" stroke-width="0.0353982"/>
                            <path stroke="${track.colorCode}" d="M9.91162 11.7522L11.0698 12.824C11.0848 12.8378 11.1084 12.8362 11.1213 12.8204L13.6107 9.7699" stroke-opacity="0.6" stroke-width="1.13274" stroke-linecap="round"/>
                        </svg>
                        <span style="margin-left: 0.25rem;">${track.userName}</span>
                    </div>
                    <span class="btn-silver-hover" style="font-size: 0.8rem; margin-left: 2.5rem;"
                        onclick="window.${this.planningMapComponentName}.showAllPerformerRequests(${track.userId})"
                        >Все заявки</span>
                </div>`;

        return `
            <div style="padding: 0.25rem;">
                ${timeContent}
                ${performerUserContent}
            </div>`;
    }

    public showAllPerformerRequests(userId: number) {
        this.setPerformerUser.emit(userId);
    }

    public openRequest(requestId: number) {
        window.open(`/requests/${requestId}#activities`, '_blank');
    }

    public setRequestPerformerUser(requestId: number) {
        if (this.securityService.isSpecialRightClaimValid('FullReadonly')) {
            return;
        }

        this.setVisitDate.emit(requestId);
    }

    public resetRequestPerformerUser(requestId: number) {
        if (this.securityService.isSpecialRightClaimValid('FullReadonly')) {
            return;
        }

        this.setVisitDate.emit(requestId);
    }

    private isEmpty(value: string): boolean {
        return value == null || value.trim() == '';
    }
}